import jwtDecode from 'jwt-decode';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const isRefreshTokenUsable = (refreshToken: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(refreshToken);
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    // Malformed/corrupt token — treat as stale so the caller purges it
    return false;
  }
};

// Drop the persisted auth slice before redux-persist REHYDRATE when the saved
// refresh token is expired or corrupt. Without this, the booking calendar
// renders against a dead session and shows every date as unavailable while
// the reauth pipeline races to log the user out.
//
// Mirrors the in-app reset() reducer (store/index.ts): auth keeps only
// apiBaseURL/selectedCountry, remoteConfig survives, every other slice
// rehydrates fresh.
//
// Scope: this only catches tokens that are expired by their `exp` claim. A
// refresh token invalidated server-side by rotation (the dual-session
// clobbering case) still has a valid `exp` — that path is handled at runtime
// by the 401 → logout → redirect flow in baseApiWithReauth.
export const purgePersistedAuthIfStale = (storage: StorageLike, persistKey: string): void => {
  try {
    const raw = storage.getItem(persistKey);
    if (!raw) return;
    const slices = JSON.parse(raw) as Record<string, string>;
    if (typeof slices?.auth !== 'string') return;
    const auth = JSON.parse(slices.auth) as Record<string, unknown>;
    const refreshToken = auth?.refreshToken;
    if (typeof refreshToken !== 'string' || !refreshToken) return;
    if (isRefreshTokenUsable(refreshToken)) return;

    const preservedAuth: Record<string, unknown> = {};
    if (auth.apiBaseURL !== undefined) preservedAuth.apiBaseURL = auth.apiBaseURL;
    if (auth.selectedCountry !== undefined) preservedAuth.selectedCountry = auth.selectedCountry;
    const purged: Record<string, string> = { auth: JSON.stringify(preservedAuth) };
    if (typeof slices.remoteConfig === 'string') purged.remoteConfig = slices.remoteConfig;
    if (typeof slices._persist === 'string') purged._persist = slices._persist;
    storage.setItem(persistKey, JSON.stringify(purged));

    storage.removeItem('email');
    storage.removeItem('new_email');
  } catch {
    // best-effort — never block app boot
  }
};
